import axios, { type InternalAxiosRequestConfig } from 'axios'
import { supabase } from '@/supabase'
import { registerGuestAxiosGuard } from '@/guest/egressGuard'
import { getDevHarnessHeaders } from '@/utils/devHarnessHeaders'
import { getImpersonationHeaders } from '@/utils/impersonationHeaders'
import {
    getAuthForbiddenFromResponse,
    isUnauthorizedAuthResponse,
    markAuthRequestRetried,
    redirectToLoginAfterSessionExpired,
    refreshAccessToken,
    shouldRetryAuthRequest,
} from '@/utils/supabaseAuthRecovery'
import { isDesktopApp } from '@/desktop/bridge'
import { isOfflineNow } from '@/desktop/useConnectivity'
import { withPlatformHeaders } from '@/native/platformHeaders'
import { notifyWebOnly, isWebOnlyResponse } from '@/native/webOnly'
import { isSupportViewEmailHiddenResponse, noteSupportViewEmailHidden } from '@/utils/supportView'

const httpClient = axios.create({
    baseURL: import.meta.env.VITE_APP_BACKEND_URL,
})

// Fail-closed in guest mode — every httpClient call is blocked.
registerGuestAxiosGuard(httpClient)

httpClient.interceptors.request.use(async (request) => {
    const access_token = (await supabase.auth.getSession())?.data?.session?.access_token
    if (access_token) {
        request.headers.Authorization = `Bearer ${access_token}`
        if (!(request.data instanceof FormData)) {
            request.headers['Content-Type'] = 'application/json'
        }
    }

    // Staff impersonation uses the target user's JWT; tag requests so activity
    // middleware can skip last_active_at / activity-session writes.
    for (const [key, value] of Object.entries(getImpersonationHeaders())) {
        request.headers[key] = value
    }

    // Dev harness browsing should not inflate product activity metrics.
    for (const [key, value] of Object.entries(getDevHarnessHeaders())) {
        request.headers[key] = value
    }

    // Store builds: X-Lighthouse-Platform + X-Lighthouse-App-Version on every call (src/native/platformHeaders.ts).
    return withPlatformHeaders(request)
})

httpClient.interceptors.response.use(
    response => response,
    async (error) => {
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
            return Promise.reject(error)
        }
        if (!error.response) {
            // No response at all. In the desktop app, when the device is offline say
            // so (status 0), so callers can tell "no network" from a server error and
            // retry later. The web and native apps keep the plain 500.
            if (isDesktopApp() && isOfflineNow()) {
                return {
                    status: 0,
                    offline: true,
                }
            }
            return {
                status: 500,
            }
        }

        const originalRequest = error.config as InternalAxiosRequestConfig & {
            _authRetry?: boolean
        }

        if (getAuthForbiddenFromResponse(error.response) && originalRequest) {
            // 401 from validate_user means the Supabase session is gone/revoked
            // (e.g. "session_id claim in JWT does not exist") — refresh won't help.
            const sessionGone = isUnauthorizedAuthResponse(
                error.response.status,
                error.response.data,
            )
            if (!sessionGone && shouldRetryAuthRequest(originalRequest)) {
                markAuthRequestRetried(originalRequest)
                const accessToken = await refreshAccessToken()
                if (accessToken) {
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`
                    return httpClient(originalRequest)
                }
            }
            await redirectToLoginAfterSessionExpired()
        }

        // Support view (staff impersonation without email access): the backend refuses email content with 403
        // `support_view_email_hidden`. Switch this tab to the email-hidden state and stay quiet: the surfaces show
        // a placeholder, and error toasts from the caller are muted for a moment (src/utils/supportView.ts).
        if (isSupportViewEmailHiddenResponse(error.response.status, error.response.data)) {
            noteSupportViewEmailHidden()
            return error.response
        }

        // Purchases are web-only: the backend answers 403 `web_only` to the apps. Say so neutrally, never crash.
        if (isWebOnlyResponse(error.response.status, error.response.data)) {
            notifyWebOnly()
            return error.response
        }

        if (error.response.status >= 400 && error.response.status < 500) {
            console.log(loggable(error.response))
        }
        else if (error.response.status >= 500) {
            console.log(loggable(error.response))
        }
        return error.response
    },
)

/** A failed response for the console, without what a desktop client-call answer sent (the creator's clipboard). */
function loggable<T extends { config?: { url?: string; data?: unknown } }>(response: T): T {
    const config = response.config
    if (!config || !/\/client-calls\//.test(String(config.url ?? ''))) return response
    return { ...response, config: { ...config, data: undefined } } as T
}

export default httpClient
